import React from "react";
import Select from "react-select";

import LabelChipsInput, { isLabelsPromptField } from "./LabelChipsInput";
import {
  AddDatasetIcon,
  CheckCircleIcon,
  CloneIcon,
  GearIcon,
  ModelTrainingIcon,
  PlatformIcon,
  TransferTitleIcon,
  UploadIcon,
} from "../../Common/SvgIcons/WorkflowIcons";
import { CheckIcon } from "lucide-react";

const formatInlineValidationError = (message = "") =>
  String(message || "")
    .replace(/\s*\(items ## \d+ and \d+ are identical\)/gi, "")
    .trim();

export const isNamePromptLabelArray = (fieldSchema = {}) =>
  fieldSchema?.type === "array" &&
  fieldSchema?.items?.type === "object" &&
  Boolean(
    fieldSchema.items?.properties?.name ||
      fieldSchema.items?.properties?.prompt ||
      fieldSchema.items?.properties?.description
  );

export const LabelsPromptEditor = ({
  title = "Labels",
  description = "",
  value = [],
  onChange,
  itemSchema = {},
  required = false,
  showErrors = false,
  errorMessage = "",
}) => {
  const items = Array.isArray(value) ? value : [];
  const nameSchema = itemSchema?.properties?.name || {};
  const promptSchema =
    itemSchema?.properties?.prompt || itemSchema?.properties?.description || {};
  const promptKey = itemSchema?.properties?.prompt
    ? "prompt"
    : itemSchema?.properties?.description
      ? "description"
      : "prompt";
  const nameRequired = Boolean(itemSchema?.required?.includes("name") || required);

  const updateItem = (idx, patch) => {
    const next = items.map((item, itemIdx) =>
      itemIdx === idx ? { ...(item || {}), ...patch } : item
    );
    onChange?.(next);
  };

  const addItem = () => {
    onChange?.([
      ...items,
      {
        name: "",
        [promptKey]: "",
      },
    ]);
  };

  const removeItem = (idx) => {
    onChange?.(items.filter((_, itemIdx) => itemIdx !== idx));
  };

  return (
    <div className="dataset-labels-prompt-field">
      <div>
        <div className="bold-text mb-1">
          {title}
          {required && <span className="text-danger ms-1">*</span>}
        </div>
        {description ? <div className="form-help mb-0">{description}</div> : null}
        {showErrors && errorMessage ? (
          <div className="text-danger small fw-semibold mt-1">{errorMessage}</div>
        ) : null}
      </div>

      {items.length === 0 ? (
        <div className="small-text-secondary">No labels added yet.</div>
      ) : (
        items.map((item, idx) => {
          const nameValue = item?.name ?? "";
          const promptValue = item?.[promptKey] ?? "";
          const nameMissing =
            showErrors && nameRequired && !String(nameValue || "").trim();

          return (
            <div className="dataset-labels-prompt-field__item" key={`label-${idx}`}>
              <div className="dataset-labels-prompt-field__item-header">
                <span className="small-text-secondary">Label {idx + 1}</span>
                <button
                  type="button"
                  className="plain-button2 text-danger p-0"
                  onClick={() => removeItem(idx)}
                >
                  Remove
                </button>
              </div>

              <div className="mb-2">
                <label className="form-label mb-1">
                  {nameSchema?.title || "Name"}
                  {nameRequired && <span className="text-danger ms-1">*</span>}
                </label>
                {nameSchema?.description ? (
                  <div className="form-help mb-1">{nameSchema.description}</div>
                ) : null}
                <input
                  className={`form-control ${nameMissing ? "is-invalid" : ""}`}
                  value={nameValue}
                  placeholder="e.g. defect"
                  onChange={(e) => updateItem(idx, { name: e.target.value })}
                />
                {nameMissing ? (
                  <div className="text-danger small fw-semibold mt-1">
                    {(nameSchema?.title || "Name")} is required.
                  </div>
                ) : null}
              </div>

              <div>
                <label className="form-label mb-1">
                  {promptSchema?.title || "Prompt"}
                </label>
                {promptSchema?.description ? (
                  <div className="form-help mb-1">{promptSchema.description}</div>
                ) : null}
                <input
                  className="form-control"
                  value={promptValue}
                  placeholder="Optional prompt for this label"
                  onChange={(e) =>
                    updateItem(idx, { [promptKey]: e.target.value })
                  }
                />
              </div>
            </div>
          );
        })
      )}

      <button
        type="button"
        className="btn btn-primary dataset-labels-prompt-field__add"
        onClick={addItem}
        aria-label="Add label"
      >
        +
      </button>
    </div>
  );
};

export const LabelsPromptArrayField = (props) => {
  const {
    formData = [],
    onChange,
    schema,
    uiSchema,
    required,
    formContext,
    errorSchema,
  } = props;

  const title = uiSchema?.["ui:title"] || schema?.title || "Labels";
  const description = uiSchema?.["ui:description"] || schema?.description || "";
  const fieldErrors = errorSchema?.__errors || [];
  const showErrors = Boolean(formContext?.hasTriedSubmit);

  return (
    <LabelsPromptEditor
      title={title}
      description={description}
      value={formData}
      onChange={onChange}
      itemSchema={schema?.items || {}}
      required={required}
      showErrors={showErrors}
      errorMessage={
        fieldErrors[0] ? formatInlineValidationError(fieldErrors[0]) : ""
      }
    />
  );
};

export const sanitizeUiSchema = (uiSchemaNode) => {
  if (Array.isArray(uiSchemaNode)) {
    let wildcardSeen = false;

    return uiSchemaNode.filter((item) => {
      if (item !== "*") return true;
      if (wildcardSeen) return false;

      wildcardSeen = true;
      return true;
    });
  }

  if (!uiSchemaNode || typeof uiSchemaNode !== "object") {
    return uiSchemaNode;
  }

  return Object.keys(uiSchemaNode).reduce((acc, key) => {
    if (key === "ui:order" && Array.isArray(uiSchemaNode[key])) {
      let wildcardSeen = false;

      acc[key] = uiSchemaNode[key].filter((item) => {
        if (item !== "*") return true;
        if (wildcardSeen) return false;

        wildcardSeen = true;
        return true;
      });
      return acc;
    }

    acc[key] = sanitizeUiSchema(uiSchemaNode[key]);
    return acc;
  }, {});
};

export const replaceUnderscoresInSchemaTitles = (schemaObj) => {
  if (!schemaObj || typeof schemaObj !== "object") return schemaObj;

  const newSchema = { ...schemaObj };

  if (newSchema.title) {
    newSchema.title = newSchema.title.replace(/_/g, " ");
  }

  if (newSchema.properties) {
    newSchema.properties = Object.keys(newSchema.properties).reduce((acc, key) => {
      acc[key] = replaceUnderscoresInSchemaTitles(newSchema.properties[key]);
      return acc;
    }, {});
  }

  if (newSchema.items) {
    newSchema.items = Array.isArray(newSchema.items)
      ? newSchema.items.map((item) => replaceUnderscoresInSchemaTitles(item))
      : replaceUnderscoresInSchemaTitles(newSchema.items);
  }

  if (newSchema.dependencies) {
    newSchema.dependencies = Object.keys(newSchema.dependencies).reduce((acc, key) => {
      const dependency = newSchema.dependencies[key];

      if (Array.isArray(dependency?.oneOf)) {
        acc[key] = {
          ...dependency,
          oneOf: dependency.oneOf.map((dep) =>
            replaceUnderscoresInSchemaTitles(dep)
          ),
        };
      } else {
        acc[key] = replaceUnderscoresInSchemaTitles(dependency);
      }

      return acc;
    }, {});
  }

  return newSchema;
};

export const normalizeFieldName = (name = "") =>
  String(name || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");

const getReadableName = (name = "") =>
  String(name || "")
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2");

const getPropertyClassName = (name = "", propertyCount = 0) => {
  const normalized = normalizeFieldName(name);

  if (
    propertyCount === 1 ||
    normalized.includes("description") ||
    normalized.includes("guideline") ||
    normalized.includes("label") ||
    normalized.includes("details") ||
    normalized.includes("content")
  ) {
    return "task-field-full";
  }

  return "task-field-half";
};

export const enhanceTaskLikeUiSchema = (schemaNode, uiNode = {}) => {
  if (!schemaNode || typeof schemaNode !== "object") {
    return uiNode || {};
  }

  const nextUiNode = { ...(uiNode || {}) };

  if (schemaNode.type === "boolean" && !nextUiNode["ui:widget"]) {
    nextUiNode["ui:widget"] = "taskCheckboxCard";
  }

  if (schemaNode.properties && typeof schemaNode.properties === "object") {
    Object.keys(schemaNode.properties).forEach((key) => {
      nextUiNode[key] = enhanceTaskLikeUiSchema(
        schemaNode.properties[key],
        nextUiNode[key]
      );
    });
  }

  if (schemaNode.items && typeof schemaNode.items === "object") {
    nextUiNode.items = enhanceTaskLikeUiSchema(schemaNode.items, nextUiNode.items);
  }

  if (schemaNode.dependencies && typeof schemaNode.dependencies === "object") {
    Object.keys(schemaNode.dependencies).forEach((depKey) => {
      const dependency = schemaNode.dependencies[depKey];

      if (Array.isArray(dependency?.oneOf)) {
        nextUiNode[depKey] = {
          ...(nextUiNode[depKey] || {}),
          oneOf: dependency.oneOf.map((branch, index) =>
            enhanceTaskLikeUiSchema(
              branch,
              nextUiNode?.[depKey]?.oneOf?.[index]
            )
          ),
        };
      }
    });
  }

  return nextUiNode;
};

export const TaskLikeFieldTemplate = (fieldProps) => {
  const {
    id,
    classNames,
    label,
    name,
    required,
    description,
    errors,
    help,
    children,
    hidden,
    schema: fieldSchema,
    uiSchema = {},
  } = fieldProps;

  if (hidden) {
    return <>{children}</>;
  }

  const options = uiSchema?.["ui:options"] || {};
  const resolvedLabel = getReadableName(
    fieldSchema?.title || label || name || ""
  );
  const isStructuredField =
    fieldSchema?.type === "object" || fieldSchema?.type === "array";
  const shouldHideMeta =
    uiSchema?.["ui:label"] === false ||
    options.label === false ||
    uiSchema?.["ui:widget"] === "hidden" ||
    fieldSchema?.type === "boolean";

  return (
    <div
      className={`${classNames || ""} task-collection-field ${isStructuredField ? "task-collection-field--group" : ""
        } ${!resolvedLabel ? "task-collection-field--no-label" : ""}`}
    >
      {resolvedLabel && !shouldHideMeta && (
        <label htmlFor={id} className="form-label">
          {resolvedLabel}
          {required && <span className="text-danger ms-1">*</span>}
        </label>
      )}
      {description && !shouldHideMeta && (
        <div className="form-help">{description}</div>
      )}
      <div className="task-collection-field__control">{children}</div>
      {errors}
      {!shouldHideMeta && help}
    </div>
  );
};

export const TaskLikeObjectFieldTemplate = ({
  title,
  description,
  properties,
  required,
  uiSchema = {},
  schema,
}) => {
  if (uiSchema?.["ui:title"] === false) {
    return <>{properties.map((property) => property.content)}</>;
  }

  const visibleTitle = title || schema?.title;
  const shouldShowHeader = visibleTitle && visibleTitle !== "root";

  return (
    <section className="task-collection-section">
      {shouldShowHeader && (
        <div className="task-collection-section__header">
          <h5 className="task-collection-section__title mb-1">
            {getReadableName(visibleTitle)}
            {required && <span className="text-danger ms-1">*</span>}
          </h5>
          {description && (
            <div className="task-collection-section__description">
              {description}
            </div>
          )}
        </div>
      )}

      <div className="task-grid task-grid--two-col">
        {properties.map((property) => (
          <div
            key={property.name}
            className={`task-grid__item ${getPropertyClassName(
              property.name,
              properties.length
            )}`}
          >
            {property.content}
          </div>
        ))}
      </div>
    </section>
  );
};

export const TaskSelectWidget = ({
  schema,
  value,
  options,
  placeholder,
  disabled,
  readonly,
  required,
  uiSchema,
  onChange,
  styles,
}) => {
  const enumOptions = options?.enumOptions || [];
  const isMulti = schema?.type === "array";
  const selected = isMulti
    ? enumOptions.filter(
      (option) => Array.isArray(value) && value.includes(option.value)
    )
    : enumOptions.find((option) => option.value === value) || null;

  const titleHint = String(schema?.title || "option")
    .replace(/^select\s+/i, "")
    .replace(/^add\s+/i, "")
    .trim();
  const schemaTitleNorm = String(schema?.title || "")
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
  const isSelectDefectsField =
    schemaTitleNorm.includes("selectdefect") ||
    (schemaTitleNorm.includes("defect") && schemaTitleNorm.includes("select"));
  // Always allow clearing Select Defects so a station can be un-allocated / skipped
  const allowClear =
    isSelectDefectsField ||
    uiSchema?.["ui:options"]?.clearable === true ||
    (uiSchema?.["ui:options"]?.clearable !== false && required === false);

  const menuSurface = "var(--bg-color8, var(--bg-color))";
  const textColor = "var(--text-color)";

  const mergedStyles = {
    ...(styles || {}),
    menuPortal: (base) => ({
      ...base,
      ...(styles?.menuPortal?.(base) || {}),
      zIndex: 10050,
    }),
    menu: (base, state) => {
      const next = styles?.menu ? styles.menu(base, state) : base;
      return {
        ...next,
        zIndex: 10050,
        background: next.background || menuSurface,
        border: next.border || "1px solid var(--glass-color)",
        overflow: next.overflow || "hidden",
      };
    },
    menuList: (base, state) => {
      const next = styles?.menuList ? styles.menuList(base, state) : base;
      return {
        ...next,
        background: next.background || menuSurface,
      };
    },
    option: (base, state) => {
      const optionBackground = state.isSelected
        ? "var(--leading-color)"
        : state.isFocused
          ? `color-mix(in srgb, var(--leading-color) 14%, ${menuSurface})`
          : menuSurface;
      const themed = {
        ...base,
        cursor: "pointer",
        // Keep selected text theme-aware so it remains readable even if a
        // browser/portal rule replaces React Select's selected background.
        color: textColor,
        background: optionBackground,
        backgroundColor: optionBackground,
      };
      const next = styles?.option ? styles.option(base, state) : themed;
      return {
        ...themed,
        ...next,
        color: next.color || themed.color,
        background: next.background || themed.background,
        backgroundColor:
          next.backgroundColor || next.background || themed.backgroundColor,
      };
    },
    singleValue: (base, state) => {
      const next = styles?.singleValue ? styles.singleValue(base, state) : base;
      return {
        ...next,
        color: next.color || textColor,
      };
    },
    placeholder: (base, state) => {
      const next = styles?.placeholder ? styles.placeholder(base, state) : base;
      return {
        ...next,
        color: next.color || "var(--text-secondary, var(--text-muted))",
      };
    },
    input: (base, state) => {
      const next = styles?.input ? styles.input(base, state) : base;
      return {
        ...next,
        color: next.color || textColor,
      };
    },
  };

  return (
    <Select
      classNamePrefix="task-select"
      isMulti={isMulti}
      value={selected}
      options={enumOptions}
      isDisabled={disabled || readonly}
      isClearable={allowClear && !isMulti}
      isSearchable={false}
      styles={mergedStyles}
      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
      menuPosition="fixed"
      menuPlacement="auto"
      menuShouldScrollIntoView={false}
      closeMenuOnScroll={false}
      blurInputOnSelect={!isMulti}
      maxMenuHeight={240}
      placeholder={placeholder || `Select ${titleHint || "option"}...`}
      onChange={(option) => {
        if (isMulti) {
          onChange(Array.isArray(option) ? option.map((item) => item.value) : []);
          return;
        }

        // Clear (X) → undefined so the station is treated as un-allocated
        onChange(option == null ? undefined : option.value);
      }}
    />
  );
};

export const TaskCheckboxWidget = ({
  value,
  onChange,
  disabled,
  readonly,
  schema,
}) => {
  const checked = Boolean(value);
  const canEdit = !disabled && !readonly;

  return (
    <button
      type="button"
      className={`task-checkbox-widget ${checked ? "is-checked" : ""}`}
      disabled={!canEdit}
      onClick={() => canEdit && onChange(!checked)}
    >
      <span
        className={`task-checkbox-widget__icon ${checked ? "text-primary" : "text-secondary"
          }`}
      >
        <CheckIcon className="wf-icon" />
      </span>
      <span className="task-checkbox-widget__label">
        {schema?.title || "Enabled"}
      </span>
    </button>
  );
};

const datasetFlowSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 48,
    borderRadius: 8,
    borderColor: state.isFocused ? "var(--leading-color)" : "var(--glass-color)",
    boxShadow: state.isFocused
      ? "0 0 0 0.18rem color-mix(in srgb, var(--leading-color) 16%, transparent)"
      : "none",
    background: "var(--bg-color)",
    color: "var(--text-color)",
    cursor: "pointer",
    ":hover": {
      borderColor: "var(--leading-color)",
    },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "0 14px",
  }),
  singleValue: (base) => ({
    ...base,
    color: "var(--text-color)",
    fontWeight: 600,
  }),
  placeholder: (base) => ({
    ...base,
    color: "var(--text-secondary)",
  }),
  menu: (base) => ({
    ...base,
    zIndex: 10050,
    borderRadius: 8,
    background: "var(--bg-color8, var(--bg-color))",
    border: "1px solid var(--glass-color)",
    boxShadow: "0 12px 30px rgba(15, 23, 42, 0.14)",
    overflow: "hidden",
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 10050,
  }),
  multiValue: (base) => ({
    ...base,
    background: "color-mix(in srgb, var(--leading-color) 12%, transparent)",
    borderRadius: 6,
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: "var(--text-color)",
    fontWeight: 600,
  }),
  option: (base, state) => ({
    ...base,
    color: "var(--text-color)",
    backgroundColor: state.isSelected
      ? "var(--leading-color)"
      : state.isFocused
        ? "color-mix(in srgb, var(--leading-color) 10%, var(--bg-color8, var(--bg-color)))"
        : "var(--bg-color8, var(--bg-color))",
    cursor: "pointer",
  }),
  indicatorSeparator: (base) => ({
    ...base,
    background: "var(--glass-color)",
  }),
};

export const DatasetFlowSelectWidget = (props) => (
  <TaskSelectWidget {...props} styles={datasetFlowSelectStyles} />
);

/** Bootstrap-4 theme widgets render their own labels; FieldTemplate also does — keep labels only in FieldTemplate. */
export const UnlabeledTextWidget = ({
  id,
  placeholder,
  required,
  readonly,
  disabled,
  autofocus,
  value,
  onChange,
  onBlur,
  onFocus,
  options = {},
  schema = {},
  rawErrors = [],
  type,
}) => {
  const inputType =
    (type || schema.type) === "string" ? "text" : `${type || schema.type}`;

  return (
    <input
      id={id}
      type={inputType}
      className={`form-control ${rawErrors.length > 0 ? "is-invalid" : ""}`}
      placeholder={placeholder}
      autoFocus={autofocus}
      required={required}
      disabled={disabled}
      readOnly={readonly}
      list={schema.examples ? `examples_${id}` : undefined}
      value={value || value === 0 ? value : ""}
      onChange={(event) =>
        onChange(event.target.value === "" ? options.emptyValue : event.target.value)
      }
      onBlur={onBlur && ((event) => onBlur(id, event.target.value))}
      onFocus={onFocus && ((event) => onFocus(id, event.target.value))}
    />
  );
};

export const UnlabeledTextareaWidget = ({
  id,
  placeholder,
  required,
  readonly,
  disabled,
  autofocus,
  value,
  onChange,
  onBlur,
  onFocus,
  options = {},
  rawErrors = [],
}) => (
  <textarea
    id={id}
    className={`form-control ${rawErrors.length > 0 ? "is-invalid" : ""}`}
    placeholder={placeholder}
    autoFocus={autofocus}
    required={required}
    disabled={disabled}
    readOnly={readonly}
    rows={options.rows || 4}
    value={value || ""}
    onChange={(event) =>
      onChange(event.target.value === "" ? options.emptyValue : event.target.value)
    }
    onBlur={onBlur && ((event) => onBlur(id, event.target.value))}
    onFocus={onFocus && ((event) => onFocus(id, event.target.value))}
  />
);

export const UnlabeledUpDownWidget = ({
  id,
  required,
  readonly,
  disabled,
  autofocus,
  value,
  onChange,
  onBlur,
  onFocus,
  schema = {},
  rawErrors = [],
}) => (
  <input
    id={id}
    type="number"
    className={`form-control ${rawErrors.length > 0 ? "is-invalid" : ""}`}
    autoFocus={autofocus}
    required={required}
    disabled={disabled}
    readOnly={readonly}
    min={schema.minimum}
    max={schema.maximum}
    step={schema.multipleOf || 1}
    value={value || value === 0 ? value : ""}
    onChange={(event) => {
      const next = event.target.value;
      onChange(next === "" ? undefined : Number(next));
    }}
    onBlur={onBlur && ((event) => onBlur(id, event.target.value))}
    onFocus={onFocus && ((event) => onFocus(id, event.target.value))}
  />
);

export const UnlabeledRadioWidget = ({
  id,
  schema,
  options,
  value,
  required,
  disabled,
  readonly,
  onChange,
  onBlur,
  onFocus,
}) => {
  const enumOptions = options?.enumOptions || [];
  const enumDisabled = options?.enumDisabled;

  return (
    <div className="dataset-flow-radio-group" role="radiogroup">
      {enumOptions.map((option, index) => {
        const itemDisabled =
          Array.isArray(enumDisabled) && enumDisabled.indexOf(option.value) !== -1;
        const checked = option.value == value;
        const radioId = `${id}-${index}`;

        return (
          <div className="form-check" key={String(option.value)}>
            <input
              type="radio"
              className="form-check-input"
              id={radioId}
              name={id}
              value={String(option.value)}
              checked={checked}
              required={required}
              disabled={disabled || readonly || itemDisabled}
              onChange={(event) => {
                const next = event.target.value;
                onChange(schema?.type === "boolean" ? next !== "false" : next);
              }}
              onBlur={(event) => onBlur?.(id, event.target.value)}
              onFocus={(event) => onFocus?.(id, event.target.value)}
            />
            <label className="form-check-label" htmlFor={radioId}>
              {option.label}
            </label>
          </div>
        );
      })}
    </div>
  );
};

export const unlabeledRjsfWidgets = {
  BaseInput: UnlabeledTextWidget,
  TextWidget: UnlabeledTextWidget,
  EmailWidget: UnlabeledTextWidget,
  URLWidget: UnlabeledTextWidget,
  PasswordWidget: UnlabeledTextWidget,
  TextareaWidget: UnlabeledTextareaWidget,
  UpDownWidget: UnlabeledUpDownWidget,
  // Bootstrap RadioWidget prints its own title — FieldTemplate already does.
  RadioWidget: UnlabeledRadioWidget,
};

export const DatasetFlowCheckboxWidget = ({
  value,
  onChange,
  disabled,
  readonly,
  schema,
  label,
}) => {
  const checked = Boolean(value);
  const canEdit = !disabled && !readonly;
  const title = getReadableName(schema?.title || label || "Enabled");
  const description = schema?.description;

  return (
    <button
      type="button"
      className={`dataset-flow-switch-option ${checked ? "is-checked" : ""}`}
      disabled={!canEdit}
      onClick={() => canEdit && onChange(!checked)}
    >
      <span className="dataset-flow-switch-option__copy">
        <span className="bold-text">{title}</span>
        {description && (
          <span className="dataset-flow-switch-option__description">
            {description}
          </span>
        )}
      </span>
      <span className="dataset-flow-switch-option__control" aria-hidden="true">
        <span className="dataset-flow-switch-option__thumb" />
      </span>
    </button>
  );
};

export const buildTaskLikeWidgets = (baseWidgets = {}) => ({
  ...baseWidgets,
  ...unlabeledRjsfWidgets,
  SelectWidget: baseWidgets.SelectWidget || TaskSelectWidget,
  taskCheckboxCard: TaskCheckboxWidget,
});

export const DataSourceCardWidget = ({ value, onChange }) => {
  const options = [
    { value: "BrowserUpload", icon: <UploadIcon className="wf-icon" />, label: "Local PC" },
    { value: "CloudS3", icon: <AddDatasetIcon className="wf-icon" />, label: "Cloud Transfer (S3)" },
    { value: "PlatformImport", icon: <PlatformIcon className="wf-icon" />, label: "Platform Import" },
  ];

  return (
    <div className="row g-2">
      {options.map((opt) => (
        <div className="col-6 col-sm-4" key={opt.value}>
          <div
            onClick={() => onChange(opt.value)}
            className={`em-source-tile d-flex justify-content-between gap-2 ${value === opt.value ? "active cpf-hw-item--active" : ""}`}
            role="button"
          >
            <div className="d-flex flex-column align-items-start">
              {opt.icon}
              <h6 className="bold-text fw-semibold mt-1 mb-0">{opt.label}</h6>
            </div>
            <div className="cpf-hw-dot" />
          </div>
        </div>
      ))}
    </div>
  );
};

export const AddLabelsCardWidget = ({ value, onChange, options, label, schema }) => {
  const enumOptions = options?.enumOptions || [];
  const title = label || schema?.title || "Add Labels";

  return (
    <div>
      <label className="form-label">{title}</label>
      <div className="row g-2">
        {enumOptions.map((opt) => {
          const selected = value === opt.value;

          return (
            <div className="col-12 col-md-4" key={opt.value}>
              <div
                className={`em-choice-card ${selected ? "active" : ""}`}
                onClick={() => onChange(opt.value)}
                role="button"
              >
                <div className="bold-text">{opt.label}</div>
                <div className="em-choice-card__check">
                  <input type="checkbox" checked={selected} readOnly />
                </div>
                <div className="form-help">
                  {opt.value === "Comma-separated values" && "e.g. person,dog,car"}
                  {opt.value === "Detailed array" && "Add label name, description and attributes"}
                  {opt.value === "Skip for now" && "You can add later during data import"}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const DetailedLabelsField = ({ formData = [], onChange }) => {
  const labels = Array.isArray(formData) ? formData : [];

  const updateItem = (idx, patch) => {
    const next = [...labels];
    next[idx] = { ...(next[idx] || {}), ...patch };
    onChange(next);
  };

  const addEntry = () => {
    onChange([...labels, { name: "", description: "", attributes: [] }]);
  };

  const removeEntry = (idx) => {
    const next = [...labels];
    next.splice(idx, 1);
    onChange(next);
  };

  const addAttribute = (idx) => {
    const item = labels[idx] || {};
    const value = String(item.__attrDraft || "").trim();
    if (!value) return;

    const attrs = Array.isArray(item.attributes) ? item.attributes : [];
    if (attrs.includes(value)) return;

    updateItem(idx, { attributes: [...attrs, value], __attrDraft: "" });
  };

  const removeAttribute = (idx, aidx) => {
    const item = labels[idx] || {};
    const attrs = Array.isArray(item.attributes) ? [...item.attributes] : [];
    attrs.splice(aidx, 1);
    updateItem(idx, { attributes: attrs });
  };

  return (
    <div className="mt-3">
      <div className="bold-text mb-3 pb-2" style={{ borderBottom: "1px solid var(--glass-color)" }}>
        Detailed Labels
      </div>

      {labels.map((item, idx) => (
        <div
          key={idx}
          className="mb-3"
          style={{
            // border: "1px solid var(--glass-color)",
            // borderRadius: "12px",
            // background: "var(--bg-color)",
          }}
        >
          <div className="row g-3">
            <div className="w-100">
              <div className="d-a  justify-content-between">
                <label className="small">
                  Name<span className="text-danger">*</span>
                </label>

                <button
                  type="button"
                  className="plain-button2 text-danger text-small"
                  onClick={() => removeEntry(idx)}
                >
                  Remove
                </button>
              </div>
              <input
                className="form-control badge-border border-2"
                value={item?.name || ""}
                placeholder="e.g. Defect-A"
                onChange={(e) => updateItem(idx, { name: e.target.value })}
              />
            </div>


            {/* <div className="col-12 col-md-6">
              <label className="small">Description</label>
              <input
                className="form-control badge-border border-2"
                value={item?.description || ""}
                placeholder="Optional description"
                onChange={(e) => updateItem(idx, { description: e.target.value })}
              />
            </div> */}

            {/* <div className="col-12">
              <label className="small">Attributes</label>
              <div className="d-flex gap-2 align-items-center">
                <input
                  className="form-control badge-border border-2"
                  value={item?.__attrDraft || ""}
                  placeholder="Type attribute and press +"
                  onChange={(e) => updateItem(idx, { __attrDraft: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addAttribute(idx);
                    }
                  }}
                />
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => addAttribute(idx)}
                  style={{ width: "42px", height: "38px" }}
                >
                  +
                </button>
              </div>

              <div className="d-flex flex-wrap gap-2 mt-2">
                {(item?.attributes || []).map((attr, attrIdx) => (
                  <span className="badge rounded-pill bg-primary" key={`${attr}-${attrIdx}`}>
                    {attr}
                    <button
                      type="button"
                      className="plain-button2 text-white ms-2 p-0"
                      onClick={() => removeAttribute(idx, attrIdx)}
                    >
                      x
                    </button>
                  </span>
                ))}
              </div>

 
            </div> */}
          </div>
        </div>
      ))}

      <div
        role="button"
        onClick={addEntry}
        className="d-flex flex-column align-items-center justify-content-center"
        style={{
          // border: "2px solid var(--glass-color)",
          // borderRadius: "14px",
          // padding: "28px 12px",
          cursor: "pointer",
        }}
      >
        <div
          className="d-flex align-items-center justify-content-center mb-2"
          style={{
            width: 40,
            height: 40,
            borderRadius: 12,
            border: "1px solid var(--glass-color)",
          }}
        >
          +
        </div>
        <div className="text-uppercase small text-secondary fw-semibold">
          Add another label entry
        </div>
      </div>
    </div>
  );
};

export const ObjectDetailsField = ({ formData = {}, onChange, errorSchema }) => {
  const labelsErrors = errorSchema?.labels?.__errors || [];

  return (
    <div className="mt-4">
      <strong className="mb-3 d-flex pb-2" style={{ borderBottom: "1px solid var(--glass-color)" }}>
        Object Details
      </strong>

      <div className="mb-2">
        {labelsErrors.length > 0 && (
          <div className="text-danger small fw-semibold mb-1">
            {"Labels " + labelsErrors[0]}
          </div>
        )}
        <label className="small">
          Labels<span className="text-danger">*</span>
        </label>
        <LabelChipsInput
          value={formData.labels || ""}
          invalid={labelsErrors.length > 0}
          onChange={(nextLabels) => onChange({ ...formData, labels: nextLabels })}
        />
      </div>
    </div>
  );
};

export const AddDataField = ({ idSchema, title, formData, onChange, schema }) => {
  const isBoolean = schema?.type === "boolean";
  const value = formData ?? (isBoolean ? true : "Yes");

  return (
    <div className="mb-3">
      <label className="form-label">{title || "Add Data"}</label>
      <div className="d-flex gap-3">
        <label className="d-flex align-items-center gap-2">
          <input
            type="radio"
            name={idSchema.$id}
            checked={isBoolean ? value === true : value === "Yes"}
            onChange={() => onChange(isBoolean ? true : "Yes")}
          />
          Yes
        </label>
        <label className="d-flex align-items-center gap-2">
          <input
            type="radio"
            name={idSchema.$id}
            checked={isBoolean ? value === false : value === "No"}
            onChange={() => onChange(isBoolean ? false : "No")}
          />
          No
        </label>
      </div>
    </div>
  );
};

export const ContentCardWidget = ({ value, onChange, schema }) => {
  const enums = schema.enum || [];
  const enumNames = schema.enumNames || enums;
  const options = enums.map((val, idx) => {
    const label = enumNames[idx] || val;
    const match = label.match(/^(.+?)(?:\s*\((.+)\))?$/);

    return {
      value: val,
      title: match?.[1] ?? label,
      description: match?.[2] ?? "",
    };
  });

  return (
    <div className="row g-3">
      {options.map((opt) => {
        const selected = value === opt.value;

        return (
          <div className="col-12 col-md-4" key={opt.value}>
            <div
              role="button"
              onClick={() => onChange(opt.value)}
              className={`content-card ${selected ? "active" : ""}`}
            >
              <div className="content-card__radio">
                <input type="radio" checked={selected} readOnly />
              </div>
              <div className="bold-text">{opt.title}</div>
              {opt.description && (
                <small className="small-text-secondary m-0">{opt.description}</small>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const getExperimentChoiceMeta = (label = "", value = "") => {
  const normalized = `${label} ${value}`.toLowerCase();

  if (normalized.includes("clone")) {
    return {
      icon: <CloneIcon className="wf-icon" />,
      eyebrow: "Reuse setup",
      title: "Clone Experiment",
      description: "Start with an existing run and adjust only what changed.",
    };
  }

  if (normalized.includes("transfer")) {
    return {
      icon: <TransferTitleIcon className="wf-icon" />,
      eyebrow: "Model reuse",
      title: "Transfer Learning",
      description: "Fine-tune from a trained model when you have a baseline.",
    };
  }

  if (normalized.includes("advanced") || normalized.includes("advance")) {
    return {
      icon: <GearIcon className="wf-icon" />,
      eyebrow: "Full control",
      title: "Advanced",
      description: "Expose model, data, and training parameters for a custom run.",
    };
  }

  if (normalized.includes("basic")) {
    return {
      icon: <CheckCircleIcon className="wf-icon" />,
      eyebrow: "Guided setup",
      title: "Basic",
      description: "Use recommended defaults and move through only the essentials.",
    };
  }

  return {
    icon: <ModelTrainingIcon className="wf-icon" />,
    eyebrow: "Fresh run",
    title: "New Experiment",
    description: "Create a clean training experiment with a new configuration.",
  };
};

export const ExperimentContentCardWidget = ({ value, onChange, schema }) => {
  const enums = schema.enum || [];
  const enumNames = schema.enumNames || enums;
  const options = enums.map((val, idx) => {
    const label = enumNames[idx] || val;
    const match = String(label).match(/^(.+?)(?:\s*\((.+)\))?$/);
    const title = match?.[1] ?? label;
    const description = match?.[2] ?? "";
    const meta = getExperimentChoiceMeta(title, val);

    return {
      value: val,
      title: meta.title || title,
      description: description || meta.description,
      eyebrow: meta.eyebrow,
      icon: meta.icon,
    };
  });

  return (
    <div className="row g-3 experiment-choice-grid">
      {options.map((opt) => {
        const selected = value === opt.value;

        return (
          <div className="col-12" key={opt.value}>
            <button
              type="button"
              onClick={() => onChange(opt.value)}
              className={`experiment-choice-card ${selected ? "active" : ""}`}
            >
              <span className="experiment-choice-card__icon">{opt.icon}</span>
              <span className="experiment-choice-card__copy">
                <span className="experiment-choice-card__eyebrow">{opt.eyebrow}</span>
                <span className="experiment-choice-card__title">{opt.title}</span>
                {opt.description && (
                  <span className="experiment-choice-card__desc">{opt.description}</span>
                )}
              </span>
              <span className="experiment-choice-card__check" aria-hidden="true">
                <span />
              </span>
            </button>
          </div>
        );
      })}
    </div>
  );
};

export const ExperimentTechnicalOptionWidget = ({ value, onChange, schema }) => {
  const enums = schema.enum || [];
  const enumNames = schema.enumNames || enums;
  const stacked = enums.length >= 3;
  const pair = enums.length === 2;

  return (
    <div
      className={`experiment-technical-options ${
        stacked ? "experiment-technical-options--stack" : ""
      } ${pair ? "experiment-technical-options--pair" : ""}`}
      role="radiogroup"
    >
      {enums.map((val, idx) => {
        const label = enumNames[idx] || val;
        const selected = value === val;

        return (
          <button
            type="button"
            key={val}
            className={`experiment-technical-option ${selected ? "active" : ""}`}
            onClick={() => onChange(val)}
          >
            <span className="experiment-technical-option__title">{label}</span>
            <span className="experiment-technical-option__check" aria-hidden="true">
              <span />
            </span>
          </button>
        );
      })}
    </div>
  );
};

export const AnnotationGuidelinesWidget = ({
  value,
  onChange,
  disabled,
  readonly,
  schema,
}) => {
  const fileInputRef = React.useRef(null);
  const [dragOver, setDragOver] = React.useState(false);
  const [fileName, setFileName] = React.useState("");
  const canEdit = !disabled && !readonly;
  const supportedText = "Supported file formats: .pdf, .doc, .docx, .txt, .jpg, .jpeg, .png";

  const addFileMetadata = (dataUrl, file) => {
    const separator = dataUrl.indexOf(",");
    if (separator < 0) return dataUrl;
    const header = dataUrl.slice(0, separator).replace(
      ";base64",
      `;name=${encodeURIComponent(file.name)};base64`
    );
    return `${header}${dataUrl.slice(separator)}`;
  };

  const readFile = (file) => {
    if (!file || !canEdit) return;

    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = () => onChange(addFileMetadata(reader.result, file));
    reader.readAsDataURL(file);
  };

  const handleFileChange = (event) => {
    readFile(event.target.files?.[0]);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragOver(false);
    readFile(event.dataTransfer.files?.[0]);
  };

  return (
    <div className="file-upload-container">
      <div className="w-100 h-100 bg-transparent">
        <div
          className="em-dropzone text-center d-a flex-column h-100 p-5"
          style={{
            background: "transparent",
            border: dragOver
              ? "2px dashed var(--leading-color)"
              : "2px dashed var(--glass-color)",
            cursor: canEdit ? "pointer" : "default",
          }}
          onDragOver={(event) => {
            event.preventDefault();
            if (canEdit) setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => canEdit && fileInputRef.current?.click()}
          role="button"
          aria-label={schema?.title || "Upload Annotation Guidelines"}
        >
          <UploadIcon className="em-drop-icon mb-2 wf-icon" />
          <p className="bold-text mb-0">
            {fileName || value ? "Guidelines file selected" : "Drag & Drop files"}
          </p>
          <p className="em-drop-subtext">
            {fileName || "or browse your computer"}
          </p>
          <p className="small-text-secondary mb-0">
          Supported file formats: {supportedText}
          </p>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,image/jpeg,image/png"
          style={{ display: "none" }}
          onChange={handleFileChange}
          disabled={!canEdit}
        />
      </div>
    </div>
  );
};

export const ToggleCardField = ({
  idSchema,
  title,
  formData,
  onChange,
  schema,
  uiSchema,
  formContext,
}) => {
  const value = Boolean(formData);
  const rawName = idSchema.$id?.split("_").pop()?.replace(/_/g, " ");
  const fieldTitle = uiSchema?.["ui:title"] || title || schema?.title || rawName;
  const fieldDescription = uiSchema?.["ui:description"] || schema?.description;
  const fullSchema = formContext?.schema;
  const isAvoidSimilarImages = rawName === "Avoid Similar Images";
  const isUseAIAnnotate = rawName === "Use AI to Annotate";

  const getFieldLabel = (fieldSchema, fallback) =>
    (fieldSchema?.title || fallback || "").replace(/_/g, " ");

  const isRequiredField = (parentSchema, fieldName) =>
    parentSchema?.required?.includes(fieldName);

  const updateNestedSection = (sectionKey, fieldKey, nextValue) => {
    formContext?.setFormData?.((prev) => ({
      ...prev,
      [sectionKey]: {
        ...(prev[sectionKey] || {}),
        [fieldKey]: nextValue,
      },
    }));
  };

  const getNestedFieldValue = (sectionKey, fieldKey, fieldSchema) => {
    const nestedValue = formContext?.formData?.[sectionKey]?.[fieldKey];
    if (nestedValue !== undefined && nestedValue !== null) return nestedValue;
    return fieldSchema?.default ?? "";
  };

  const getMatchingDependencyProperties = (parentSchema, fieldKey, fieldValue) => {
    const dependency = parentSchema?.dependencies?.[fieldKey];
    if (!Array.isArray(dependency?.oneOf)) return [];

    const selectedBranch = dependency.oneOf.find(
      (branch) => branch?.properties?.[fieldKey]?.const === fieldValue
    );

    return Object.entries(selectedBranch?.properties || {}).filter(
      ([dependentKey]) => dependentKey !== fieldKey
    );
  };

  const renderSectionField = (sectionKey, sectionSchema, fieldKey, fieldSchema) => {
    const nestedValue = getNestedFieldValue(sectionKey, fieldKey, fieldSchema);
    const label = getFieldLabel(fieldSchema, fieldKey);
    const required = isRequiredField(sectionSchema, fieldKey);

    if (fieldSchema?.enum) {
      return (
        <div className="mb-2" key={`${sectionKey}-${fieldKey}`}>
          <label className="small d-flex mb-1">
            {label}
            {required && <span className="text-danger ms-1">*</span>}
          </label>
          <select
            className="form-select"
            value={nestedValue || ""}
            onChange={(e) => updateNestedSection(sectionKey, fieldKey, e.target.value)}
          >
            <option value="">Select</option>
            {(fieldSchema.enum || []).map((optionValue, optionIndex) => (
              <option key={optionValue} value={optionValue}>
                {fieldSchema.enumNames?.[optionIndex] || optionValue}
              </option>
            ))}
          </select>
        </div>
      );
    }

    if (fieldSchema?.type === "number" || fieldSchema?.type === "integer") {
      return (
        <div className="mb-2" key={`${sectionKey}-${fieldKey}`}>
          <label className="small d-flex mb-1">
            {label}
            {required && <span className="text-danger ms-1">*</span>}
          </label>
          <input
            type="number"
            min={fieldSchema?.minimum}
            max={fieldSchema?.maximum}
            step={fieldSchema?.multipleOf || 0.01}
            className="form-control"
            value={nestedValue ?? ""}
            onChange={(e) =>
              updateNestedSection(
                sectionKey,
                fieldKey,
                e.target.value === "" ? "" : Number(e.target.value)
              )
            }
          />
        </div>
      );
    }

    if (isLabelsPromptField(fieldKey, fieldSchema)) {
      return (
        <div className="mb-2" key={`${sectionKey}-${fieldKey}`}>
          <label className="form-label mb-1">
            {label}
            {required && <span className="text-danger ms-1">*</span>}
          </label>
          <LabelChipsInput
            value={nestedValue}
            onChange={(nextLabels) => updateNestedSection(sectionKey, fieldKey, nextLabels)}
            ariaLabel={label}
          />
        </div>
      );
    }

    if (isNamePromptLabelArray(fieldSchema)) {
      return (
        <div className="mb-2" key={`${sectionKey}-${fieldKey}`}>
          <LabelsPromptEditor
            title={label}
            description={fieldSchema?.description || ""}
            value={Array.isArray(nestedValue) ? nestedValue : []}
            onChange={(nextLabels) =>
              updateNestedSection(sectionKey, fieldKey, nextLabels)
            }
            itemSchema={fieldSchema?.items || {}}
            required={required}
          />
        </div>
      );
    }

    return (
      <div className="mb-2" key={`${sectionKey}-${fieldKey}`}>
        <label className="form-label mb-1">
          {label}
          {required && <span className="text-danger ms-1">*</span>}
        </label>
        <input
          type="text"
          className="form-control"
          value={nestedValue}
          onChange={(e) => updateNestedSection(sectionKey, fieldKey, e.target.value)}
        />
      </div>
    );
  };

  const renderSectionFieldWithDependencies = (sectionKey, sectionSchema, fieldKey, fieldSchema) => {
    const nestedValue = getNestedFieldValue(sectionKey, fieldKey, fieldSchema);
    const dependentFields = getMatchingDependencyProperties(sectionSchema, fieldKey, nestedValue);

    return (
      <div key={`${sectionKey}-${fieldKey}`}>
        {renderSectionField(sectionKey, sectionSchema, fieldKey, fieldSchema)}
        {dependentFields.map(([dependentKey, dependentSchema]) => (
          <div className="ms-2" key={`${sectionKey}-${fieldKey}-${dependentKey}`}>
            {renderSectionFieldWithDependencies(sectionKey, sectionSchema, dependentKey, dependentSchema)}
          </div>
        ))}
      </div>
    );
  };

  const renderSelectedAIAssistSections = () => {
    const selectedAIAssist = formContext?.formData?.["AI Assist"];
    if (!selectedAIAssist) return null;

    const selectedDependency = fullSchema?.dependencies?.["AI Assist"]?.oneOf?.find(
      (dependency) => dependency?.properties?.["AI Assist"]?.const === selectedAIAssist
    );

    const visibleSections = Object.entries(selectedDependency?.properties || {}).filter(
      ([key, sectionSchema]) => key !== "AI Assist" && sectionSchema?.type === "object"
    );

    return visibleSections.map(([sectionKey, sectionSchema]) => (
      <div className="em-toggle-card__sub-block" key={sectionKey}>
        <label className="form-label mb-2">
          {getFieldLabel(sectionSchema, sectionKey)}
        </label>
        {Object.entries(sectionSchema?.properties || {}).map(([fieldKey, fieldSchema]) =>
          renderSectionFieldWithDependencies(sectionKey, sectionSchema, fieldKey, fieldSchema)
        )}
      </div>
    ));
  };

  const similarityValue = formContext?.formData?.["Similarity Threshold"] ?? 0.95;

  return (
    <div className="em-toggle-card mt-3">
      <div className="em-toggle-card__left">
        <div className="bold-text">{fieldTitle}</div>
        {fieldDescription && (
          <div className="form-help">{fieldDescription}</div>
        )}

        {isAvoidSimilarImages && value && (
          <div className="em-toggle-card__slider-block">
            <div className="em-toggle-card__slider-header">
              <span>Similarity Cap</span>
              <strong>{Math.round(similarityValue * 100)}%</strong>
            </div>
            <input
              type="range"
              min={0}
              max={100}
              step={1}
              value={similarityValue * 100}
              onChange={(e) =>
                formContext?.setFormData?.((prev) => ({
                  ...prev,
                  "Similarity Threshold": Number(e.target.value) / 100,
                }))
              }
              className="em-toggle-card__range"
            />
            <div className="em-toggle-card__hint">
              Excludes assets with a perceptual hash match higher than this value.
            </div>
          </div>
        )}

        {isUseAIAnnotate && value && (
          <div className="em-toggle-card__ai-block">
            <div className="mb-3">
              <label className="form-label mb-1">Task Name</label>
              <input
                className="form-control"
                value={formContext?.formData?.["Task Name"] || ""}
                onChange={(e) =>
                  formContext?.setFormData?.((prev) => ({
                    ...prev,
                    "Task Name": e.target.value,
                  }))
                }
              />
            </div>
            <div className="mb-3">
              <label className="form-label mb-1">AI Assist</label>
              <div className="d-flex gap-2 flex-wrap container-card" style={{ background: "var(--card-color)" }}>
                {(
                  fullSchema?.dependencies?.["Use AI to Annotate"]?.oneOf
                    ?.find((dep) => dep?.properties?.["AI Assist"])
                    ?.properties?.["AI Assist"]?.enum || []
                ).map((opt, i) => {
                  const selected = formContext?.formData?.["AI Assist"] === opt;
                  const label =
                    fullSchema?.dependencies?.["Use AI to Annotate"]?.oneOf
                      ?.find((dep) => dep?.properties?.["AI Assist"])
                      ?.properties?.["AI Assist"]?.enumNames?.[i] || opt;

                  return (
                    <div
                      key={opt}
                      role="button"
                      onClick={() =>
                        formContext?.setFormData?.((prev) => ({
                          ...prev,
                          "AI Assist": opt,
                        }))
                      }
                      className="d-flex align-items-center gap-2 cursor-pointer container-card rounded px-3 p-1"
                      style={{ borderColor: selected ? "var(--leading-color)" : undefined }}
                    >
                      <CheckIcon className={`wf-icon-small ${selected ? "text-primary" : "text-secondary"}`} />
                      <span className={selected ? "small-font text-primary fw-bold" : "small-font text-secondary"}>
                        {label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
            {renderSelectedAIAssistSections()}

            {(() => {
              const aiAnnotateSchema =
                fullSchema?.dependencies?.["Use AI to Annotate"]?.oneOf?.find(
                  (dependency) =>
                    dependency?.properties?.["Use AI to Annotate"]?.const === true
                ) || fullSchema?.dependencies?.["Use AI to Annotate"]?.oneOf?.[0];

              const sizingFields = [
                "Taskset Group Name",
                "Images Per Task",
                "Tasks Per Taskset",
              ];

              return sizingFields.map((fieldKey) => {
                const fieldSchema = aiAnnotateSchema?.properties?.[fieldKey];
                if (!fieldSchema || fieldSchema.type === "object") return null;

                const label = getFieldLabel(fieldSchema, fieldKey);
                const required = isRequiredField(aiAnnotateSchema, fieldKey);
                const fieldValue = formContext?.formData?.[fieldKey] ?? "";

                return (
                  <div className="mb-3" key={fieldKey}>
                    <label className="form-label mb-1">
                      {label}
                      {required && <span className="text-danger ms-1">*</span>}
                    </label>
                    <input
                      type={
                        fieldSchema.type === "number" || fieldSchema.type === "integer"
                          ? "number"
                          : "text"
                      }
                      className="form-control"
                      value={fieldValue}
                      onChange={(e) => {
                        const raw = e.target.value;
                        const nextValue =
                          fieldSchema.type === "number" || fieldSchema.type === "integer"
                            ? raw === ""
                              ? undefined
                              : Number(raw)
                            : raw;
                        formContext?.setFormData?.((prev) => ({
                          ...prev,
                          [fieldKey]: nextValue,
                        }));
                      }}
                    />
                  </div>
                );
              });
            })()}
          </div>
        )}
      </div>

      <label className="em-toggle-card__switch">
        <input type="checkbox" checked={value} onChange={() => onChange(!value)} />
        <span className="em-toggle-card__slider" />
      </label>
    </div>
  );
};

export const DatasetFlowObjectFieldTemplate = ({
  idSchema,
  title,
  description,
  properties,
  required,
  uiSchema = {},
}) => {
  if (uiSchema?.["ui:title"] === false) {
    return <>{properties.map((property) => property.content)}</>;
  }

  const isRootObject = idSchema?.$id === "root";

  return (
    <div className="rjsf-object-field">
      {!isRootObject && title && title !== "root" && (
        <label className="form-label mt-0">
          {getReadableName(title)}
          {required && <span className="text-danger ms-1">*</span>}
        </label>
      )}
      {description}
      {properties.map((property) => (
        <div key={property.name}>{property.content}</div>
      ))}
    </div>
  );
};

export const DatasetFlowArrayFieldTemplate = ({
  title,
  schema,
  items = [],
  canAdd,
  onAddClick,
  required,
  uiSchema = {},
}) => {
  const hideTitle = uiSchema?.["ui:options"]?.label === false || uiSchema?.["ui:title"] === false;
  const displayTitle = hideTitle ? "" : title || schema?.title || "";
  const readableTitle = getReadableName(displayTitle);
  // Avoid "Add Add Product" when schema title already starts with Add
  const itemNoun = readableTitle
    ? readableTitle.replace(/^add\s+/i, "").replace(/s$/i, "") || readableTitle
    : "Item";

  return (
    <div className="dataset-flow-array-field">
      {displayTitle ? (
        <div className="mb-1">
          <div className="bold-text mb-0">
            {readableTitle}
            {required && <span className="text-danger ms-1">*</span>}
          </div>
          {schema?.description ? (
            <div className="form-help mb-0">{schema.description}</div>
          ) : null}
        </div>
      ) : null}

      <div className="dataset-flow-array-field__list">
        {items.length === 0 ? (
          <div className="small-text-secondary">No items added yet.</div>
        ) : (
          items.map((item) => (
            <div
              key={item.key || item.index}
              className={`dataset-flow-array-field__item container-card p-3 ${item.className || ""}`}
            >
              <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                <span className="small-text-secondary">
                  {itemNoun} {(item.index ?? 0) + 1}
                </span>
                {item.hasRemove ? (
                  <button
                    type="button"
                    className="plain-button2 text-danger p-0"
                    onClick={item.onDropIndexClick(item.index)}
                    disabled={item.disabled}
                  >
                    Remove
                  </button>
                ) : null}
              </div>
              <div className="dataset-flow-array-field__item-body">
                {item.children}
              </div>
            </div>
          ))
        )}
      </div>

      {canAdd ? (
        <button
          type="button"
          className="btn btn-primary align-self-start"
          onClick={onAddClick}
        >
          + Add {itemNoun}
        </button>
      ) : null}
    </div>
  );
};

export const DatasetFlowFieldTemplate = ({
  id,
  children,
  displayLabel,
  hidden,
  label,
  required,
  rawErrors = [],
  help,
  uiSchema = {},
  schema,
}) => {
  if (hidden) {
    return <>{children}</>;
  }

  const hideLabel = uiSchema?.["ui:options"]?.label === false;
  const isBoolean = schema?.type === "boolean";
  const isArray = schema?.type === "array";
  // Multi-select enums render as SelectWidget — still need a field label.
  const isSelectWidget =
    uiSchema?.["ui:widget"] === "SelectWidget" ||
    uiSchema?.["ui:widget"] === "DatasetFlowSelectWidget";
  // Array template renders its own title/actions to avoid default RJSF chrome.
  const showLabel =
    displayLabel &&
    !hideLabel &&
    label &&
    !isBoolean &&
    (!isArray || isSelectWidget);
  const shouldWrap = !hideLabel || rawErrors.length > 0 || help;

  return (
    <div className={`dataset-flow-form-field ${isBoolean ? "dataset-flow-form-field--boolean" : ""} ${isArray ? "dataset-flow-form-field--array" : ""}`}>
      {showLabel && (
        <label htmlFor={id} className="form-label">
          {getReadableName(label)}
          {required && <span className="text-danger ms-1">*</span>}
        </label>
      )}
      {rawErrors.length > 0 && (
        <div className="text-danger small fw-semibold mb-1">{rawErrors[0]}</div>
      )}
      {children}
      {shouldWrap && uiSchema?.["ui:options"]?.help !== false && help}
    </div>
  );
};

export const buildDatasetFlowFields = () => ({
  AddDataField,
  DetailedLabelsField,
  ObjectDetailsField,
  ToggleCardField,
  LabelsPromptArrayField,
});

export const buildDatasetFlowWidgets = (baseWidgets = {}) => ({
  ...baseWidgets,
  ...unlabeledRjsfWidgets,
  DataSourceCardWidget,
  AddLabelsCardWidget,
  ContentCardWidget,
  ExperimentContentCardWidget,
  ExperimentTechnicalOptionWidget,
  AnnotationGuidelinesWidget,
  SelectWidget: DatasetFlowSelectWidget,
  CheckboxWidget: DatasetFlowCheckboxWidget,
});

export const enhanceDatasetFlowUiSchema = (baseUiSchema = {}) => ({
  ...baseUiSchema,
  "Select Data Source": {
    ...(baseUiSchema["Select Data Source"] || {}),
    "ui:widget": "DataSourceCardWidget",
  },
  "Add Labels": {
    ...(baseUiSchema["Add Labels"] || {}),
    "ui:widget": "AddLabelsCardWidget",
    "ui:options": { ...(baseUiSchema["Add Labels"]?.["ui:options"] || {}), label: false, description: false },
  },
  Content: {
    ...(baseUiSchema.Content || {}),
    "ui:widget": "ContentCardWidget",
    "ui:options": { ...(baseUiSchema.Content?.["ui:options"] || {}), label: false, description: false },
  },
  "Add Data": {
    ...(baseUiSchema["Add Data"] || {}),
    "ui:field": "AddDataField",
    "ui:options": { ...(baseUiSchema["Add Data"]?.["ui:options"] || {}), label: false, description: false },
  },
  Labels: {
    ...(baseUiSchema.Labels || {}),
    "ui:field": "DetailedLabelsField",
    "ui:options": { ...(baseUiSchema.Labels?.["ui:options"] || {}), label: false, description: false },
  },
  labels: {
    ...(baseUiSchema.labels || {}),
    "ui:field": "DetailedLabelsField",
    "ui:options": { ...(baseUiSchema.labels?.["ui:options"] || {}), label: false, description: false },
  },
  detailed_labels: {
    ...(baseUiSchema.detailed_labels || {}),
    "ui:field": "DetailedLabelsField",
    "ui:options": { ...(baseUiSchema.detailed_labels?.["ui:options"] || {}), label: false, description: false },
  },
  "Object Details": {
    ...(baseUiSchema["Object Details"] || {}),
    "ui:field": "ObjectDetailsField",
    "ui:options": { ...(baseUiSchema["Object Details"]?.["ui:options"] || {}), label: false, description: false },
  },
  "Upload Annotation Guidelines": {
    ...(baseUiSchema["Upload Annotation Guidelines"] || {}),
    "ui:widget": "AnnotationGuidelinesWidget",
    "ui:options": {
      ...(baseUiSchema["Upload Annotation Guidelines"]?.["ui:options"] || {}),
      help: false,
      description: false,
    },
  },
  upload_annotation_guidelines: {
    ...(baseUiSchema.upload_annotation_guidelines || {}),
    "ui:widget": "AnnotationGuidelinesWidget",
    "ui:options": {
      ...(baseUiSchema.upload_annotation_guidelines?.["ui:options"] || {}),
      help: false,
      description: false,
    },
  },
  "Avoid Similar Images": {
    ...(baseUiSchema["Avoid Similar Images"] || {}),
    "ui:field": "ToggleCardField",
    "ui:description": baseUiSchema["Avoid Similar Images"]?.["ui:description"] || "Only include unique images",
    "ui:options": { ...(baseUiSchema["Avoid Similar Images"]?.["ui:options"] || {}), label: false, description: false },
  },
  "Use AI to Annotate": {
    ...(baseUiSchema["Use AI to Annotate"] || {}),
    "ui:field": "ToggleCardField",
    "ui:description": baseUiSchema["Use AI to Annotate"]?.["ui:description"] || "Use saved/uploaded model or platform executed experiment for annotation",
    "ui:options": { ...(baseUiSchema["Use AI to Annotate"]?.["ui:options"] || {}), label: false, description: false },
  },
  "Similarity Threshold": {
    ...(baseUiSchema["Similarity Threshold"] || {}),
    "ui:widget": "hidden",
  },
  "Task Name": {
    ...(baseUiSchema["Task Name"] || {}),
    "ui:widget": "hidden",
  },
  "Taskset Group Name": {
    ...(baseUiSchema["Taskset Group Name"] || {}),
    "ui:widget": "hidden",
  },
  "Images Per Task": {
    ...(baseUiSchema["Images Per Task"] || {}),
    "ui:widget": "hidden",
  },
  "Tasks Per Taskset": {
    ...(baseUiSchema["Tasks Per Taskset"] || {}),
    "ui:widget": "hidden",
  },
  "AI Assist": {
    ...(baseUiSchema["AI Assist"] || {}),
    "ui:field": () => null,
  },
  "Model Details": {
    ...(baseUiSchema["Model Details"] || {}),
    "ui:field": () => null,
  },
  "Experiment Details": {
    ...(baseUiSchema["Experiment Details"] || {}),
    "ui:field": () => null,
  },
  "Foundational Models": {
    ...(baseUiSchema["Foundational Models"] || {}),
    "ui:field": () => null,
  },
  "Foundational Model Details": {
    ...(baseUiSchema["Foundational Model Details"] || {}),
    "ui:field": () => null,
  },
  "ui:order": baseUiSchema["ui:order"],
});

const getExperimentEnumBlob = (schemaNode = {}) =>
  `${schemaNode.title || ""} ${(schemaNode.enum || []).join(" ")} ${(
    schemaNode.enumNames || []
  ).join(" ")}`.toLowerCase();

function getExperimentEnumWidget(schemaNode, options = {}) {
  if (
    !Array.isArray(schemaNode.enum) ||
    schemaNode.enum.length < 2 ||
    schemaNode.enum.length > 4
  ) {
    return null;
  }

  const blob = getExperimentEnumBlob(schemaNode);

  if (
    /configuration|initializer|generate from|backbone|architecture|weight|manager|format|dataset/.test(
      blob
    )
  ) {
    return null;
  }

  if (options.activeStep === 0) {
    if (/basic|advanced|advance/.test(blob) && !/clone|transfer/.test(blob)) {
      return null;
    }

    if (
      /clone|transfer|fresh|new experiment|creation mode/.test(blob) ||
      schemaNode.enum.length === 3
    ) {
      return "ExperimentContentCardWidget";
    }
  }

  if (/framework|split type/.test(blob)) {
    return "ExperimentTechnicalOptionWidget";
  }

  return null;
}

function applyExperimentEnumCardWidgets(schemaNode, uiNode = {}, options = {}) {
  if (!schemaNode || typeof schemaNode !== "object") {
    return uiNode || {};
  }

  const nextUiNode = { ...(uiNode || {}) };
  const widgetName = getExperimentEnumWidget(schemaNode, options);

  if (widgetName && !nextUiNode["ui:widget"] && !nextUiNode["ui:field"]) {
    nextUiNode["ui:widget"] = widgetName;
    nextUiNode["ui:title"] =
      widgetName === "ExperimentContentCardWidget"
        ? "Choose"
        : nextUiNode["ui:title"];
    nextUiNode["ui:options"] = {
      ...(nextUiNode["ui:options"] || {}),
      label: true,
      description: false,
    };
  }

  if (schemaNode.properties && typeof schemaNode.properties === "object") {
    Object.keys(schemaNode.properties).forEach((key) => {
      nextUiNode[key] = applyExperimentEnumCardWidgets(
        schemaNode.properties[key],
        nextUiNode[key],
        options
      );
    });
  }

  if (schemaNode.items && typeof schemaNode.items === "object") {
    nextUiNode.items = applyExperimentEnumCardWidgets(
      schemaNode.items,
      nextUiNode.items,
      options
    );
  }

  if (schemaNode.dependencies && typeof schemaNode.dependencies === "object") {
    Object.keys(schemaNode.dependencies).forEach((depKey) => {
      const dependency = schemaNode.dependencies[depKey];

      if (Array.isArray(dependency?.oneOf)) {
        nextUiNode[depKey] = {
          ...(nextUiNode[depKey] || {}),
          oneOf: dependency.oneOf.map((branch, index) =>
            applyExperimentEnumCardWidgets(
              branch,
              nextUiNode[depKey]?.oneOf?.[index],
              options
            )
          ),
        };
      } else {
        nextUiNode[depKey] = applyExperimentEnumCardWidgets(
          dependency,
          nextUiNode[depKey],
          options
        );
      }
    });
  }

  return nextUiNode;
}

export const enhanceExperimentFlowUiSchema = (
  schema,
  baseUiSchema = {},
  options = {}
) => applyExperimentEnumCardWidgets(schema, { ...(baseUiSchema || {}) }, options);

function isEnumMultiSelectArray(schemaNode) {
  if (schemaNode?.type !== "array" || !schemaNode?.items) return false;

  const items = schemaNode.items;
  if (typeof items !== "object") return false;

  if (Array.isArray(items.enum) && items.enum.length > 0) return true;

  if (
    Array.isArray(items.anyOf) &&
    items.anyOf.some((branch) => Array.isArray(branch?.enum) && branch.enum.length)
  ) {
    return true;
  }

  return Boolean(
    schemaNode.uniqueItems &&
      items.type === "string" &&
      Array.isArray(items.enum)
  );
}

function applySolutionEnumCardWidgets(schemaNode, uiNode = {}) {
  if (!schemaNode || typeof schemaNode !== "object") {
    return uiNode || {};
  }

  const nextUiNode = { ...(uiNode || {}) };

  // Never reuse experiment marketing cards in Solutions — they overwrite real labels
  // with "New Experiment" / "Fresh run" copy meant for Create Experiment.
  if (nextUiNode["ui:widget"] === "ExperimentContentCardWidget") {
    delete nextUiNode["ui:widget"];
    if (nextUiNode["ui:title"] === "Choose") {
      delete nextUiNode["ui:title"];
    }
  }

  // Multi-select enums (Products / Users) must use SelectWidget, not ArrayField
  if (isEnumMultiSelectArray(schemaNode)) {
    if (!nextUiNode["ui:widget"] && !nextUiNode["ui:field"]) {
      nextUiNode["ui:widget"] = "SelectWidget";
    }
    nextUiNode["ui:options"] = {
      ...(nextUiNode["ui:options"] || {}),
      label: true,
    };

    if (schemaNode.properties && typeof schemaNode.properties === "object") {
      Object.keys(schemaNode.properties).forEach((key) => {
        nextUiNode[key] = applySolutionEnumCardWidgets(
          schemaNode.properties[key],
          nextUiNode[key]
        );
      });
    }

    return nextUiNode;
  }

  // Prefer plain selects for Solutions — keeps the form light and label-accurate
  if (
    Array.isArray(schemaNode.enum) &&
    schemaNode.enum.length >= 2 &&
    !nextUiNode["ui:widget"] &&
    !nextUiNode["ui:field"]
  ) {
    nextUiNode["ui:widget"] = "SelectWidget";
    nextUiNode["ui:options"] = {
      ...(nextUiNode["ui:options"] || {}),
      label: true,
    };
  }

  // Select Defects on this node: clearable so stations can be un-allocated
  {
    const selfKeyNorm = String(schemaNode.title || "")
      .toLowerCase()
      .replace(/[\s_-]+/g, "");
    const isSelfSelectDefects =
      selfKeyNorm.includes("selectdefect") ||
      (selfKeyNorm.includes("defect") && selfKeyNorm.includes("select"));
    if (isSelfSelectDefects && Array.isArray(schemaNode.enum)) {
      nextUiNode["ui:widget"] = "SelectWidget";
      nextUiNode["ui:placeholder"] =
        nextUiNode["ui:placeholder"] || "Select Defects...";
      nextUiNode["ui:options"] = {
        ...(nextUiNode["ui:options"] || {}),
        label: true,
        clearable: true,
      };
    }
  }

  if (schemaNode.properties && typeof schemaNode.properties === "object") {
    Object.keys(schemaNode.properties).forEach((key) => {
      nextUiNode[key] = applySolutionEnumCardWidgets(
        schemaNode.properties[key],
        nextUiNode[key]
      );

      const childSchema = schemaNode.properties[key];
      const normalizedKey = String(key || "")
        .toLowerCase()
        .replace(/[\s_-]+/g, "");
      const normalizedTitle = String(childSchema?.title || "")
        .toLowerCase()
        .replace(/[\s_-]+/g, "");
      const isSelectDefects =
        normalizedKey.includes("selectdefect") ||
        normalizedTitle.includes("selectdefect") ||
        (normalizedKey.includes("defect") && normalizedKey.includes("select"));

      // Clearable dropdown so users can un-allocate / skip a station
      if (isSelectDefects && Array.isArray(childSchema?.enum)) {
        nextUiNode[key] = {
          ...(nextUiNode[key] || {}),
          "ui:widget": "SelectWidget",
          "ui:placeholder": "Select Defects...",
          "ui:options": {
            ...(nextUiNode[key]?.["ui:options"] || {}),
            label: true,
            clearable: true,
          },
        };
      }
    });
  }

  if (schemaNode.items && typeof schemaNode.items === "object") {
    nextUiNode.items = applySolutionEnumCardWidgets(
      schemaNode.items,
      nextUiNode.items
    );
  }

  if (schemaNode.dependencies && typeof schemaNode.dependencies === "object") {
    Object.keys(schemaNode.dependencies).forEach((depKey) => {
      const dependency = schemaNode.dependencies[depKey];

      if (Array.isArray(dependency?.oneOf)) {
        nextUiNode[depKey] = {
          ...(nextUiNode[depKey] || {}),
          oneOf: dependency.oneOf.map((branch, index) =>
            applySolutionEnumCardWidgets(
              branch,
              nextUiNode[depKey]?.oneOf?.[index]
            )
          ),
        };
      } else if (dependency && typeof dependency === "object") {
        nextUiNode[depKey] = applySolutionEnumCardWidgets(
          dependency,
          nextUiNode[depKey]
        );
      }
    });
  }

  ["oneOf", "anyOf", "allOf"].forEach((key) => {
    if (!Array.isArray(schemaNode[key])) return;
    nextUiNode[key] = schemaNode[key].map((branch, index) =>
      applySolutionEnumCardWidgets(branch, nextUiNode[key]?.[index])
    );
  });

  return nextUiNode;
}

export const enhanceSolutionFlowUiSchema = (schema, baseUiSchema = {}) =>
  applySolutionEnumCardWidgets(schema, { ...(baseUiSchema || {}) });
